import { useState, useEffect, useCallback } from 'react';
import { logmarData } from '../../../helpers/data';
import { useTestContext } from '../../../context/testContext';
import { getRandomEScore } from '../../../helpers/functions';
import { useCalibration } from '../../../context/calibrationContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { Button, Flex, Image, ResponsiveAcuityInfoPanel, Text, theme } from '../../../2022-component-library';
import { getLogmarLineSize500mm } from '../../../helpers/functions';
import leftE from '../../../assets/img/leftE.bmp';
import rightE from '../../../assets/img/rightE.bmp';
import topE from '../../../assets/img/topE.bmp';
import bottomE from '../../../assets/img/bottomE.bmp';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';

const ResponsiveRandomETestOptician = ({ goToTestResults, eyeTested }) => {
  const {
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected },
    updateCalibrationData,
  } = useCalibration();
  const { testData, setTestFieldValue, updateTestData } = useTestContext();
  const [currentLogmar, setCurrentLogmar] = useState(testData?.eyeTested);
  const [testMode, setTestMode] = useState(methodSelected !== 'manual' ? 'auto' : testData.testMode);
  const messageFromPatient = useReceiveMessage();
  const postMessage = usePostMessage();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentE, setCurrentE] = useState();
  const characterPositions = [leftE, bottomE, rightE, topE];
  const [incorrectSizeCounter, setIncorrectSizeCounter] = useState({ logmarId: 1, counter: 0 });
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const { patientDistanceReference } = calibrationConstants;

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const getLogmarContentSize = (logmar, distance) => {
    let lineSize;
    if (patientDistanceReference === 500) {
      lineSize = getLogmarLineSize500mm(logmar.id);
    }
    const contentSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
    return contentSize;
  };

  const updateLogmarContentSize = (logmar, content, distance) => {
    const contentSize = Math.round(getLogmarContentSize(logmar, distance));
    postMessage({
      type: 'resizeLogmarImage',
      content: {
        contentSize: contentSize,
        distanceReferenceInMM: distance,
        logmarContent: content,
      },
    });
  };

  const selectNextLogmar = useCallback(() => {
    if (currentLogmar.id < 13) {
      const logmar = logmarData.find((logmar) => logmar.id === currentLogmar.id + 1);
      const newE = changeE();
      setCurrentLogmar(logmar ? logmar : currentLogmar);
      updateLogmarContentSize(logmar, newE, distanceFromScreen);
    } else {
      finishTest();
    }
  }, [currentLogmar, distanceFromScreen, updateLogmarContentSize]);

  const selectPreviousLogmar = useCallback(() => {
    if (currentLogmar.id > 1 && currentLogmar.id > testData.maxPermittedLogmar.id) {
      const logmar = logmarData.find((logmar) => logmar.id === currentLogmar.id - 1);
      const newE = changeE();
      setCurrentLogmar(logmar ? logmar : currentLogmar);
      updateLogmarContentSize(logmar, newE, distanceFromScreen);
    }
  }, [currentLogmar, distanceFromScreen, updateLogmarContentSize, testData.maxPermittedLogmar.id]);

  const changeTestMode = useCallback(
    (selectedTestMode) => {
      if (selectedTestMode !== testMode) {
        updateTestData({ testMode: selectedTestMode });
        setTestMode(selectedTestMode);
        postMessage({
          type: 'updateTestMode',
          content: {
            testMode: selectedTestMode,
          },
        });
      }
    },
    [testMode, updateTestData, postMessage],
  );

  const markIncorrectAnswer = useCallback(() => {
    if (currentLogmar.id === incorrectSizeCounter?.logmarId) {
      if (incorrectSizeCounter?.counter > 0) {
        finishTest();
      }
    }

    setIncorrectSizeCounter({ logmarId: currentLogmar.id, counter: 1 });
    selectPreviousLogmar();
  }, [currentLogmar, incorrectSizeCounter?.logmarId, incorrectSizeCounter?.counter]);

  const finishTest = useCallback(() => {
    let logmarForScore = currentLogmar.id - 1;
    if (currentLogmar.id === 13) {
      logmarForScore = 13;
    }
    const score = { score: getRandomEScore(logmarForScore) };
    setTestFieldValue({
      name: eyeTested,
      value: score,
    });
    goToTestResults();
  }, [currentLogmar, eyeTested]);

  const markRepeatAnswer = useCallback(() => {
    const newE = changeE();
    updateLogmarContentSize(currentLogmar, newE, distanceFromScreen);
  }, [currentLogmar, distanceFromScreen]);

  const changeE = useCallback(() => {
    let currentGeneratedE = characterPositions[Math.floor(Math.random() * 4)];
    while (currentE === currentGeneratedE) {
      currentGeneratedE = characterPositions[Math.floor(Math.random() * 4)];
    }
    setCurrentE(currentGeneratedE);

    return currentGeneratedE;
  }, [currentE]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      const { patientDistance, patientIsMoving } = messageFromPatient.content;
      updateCalibrationData({ distanceFromScreen: patientDistance });

      if (patientIsMoving === false) {
        const newE = changeE();
        updateLogmarContentSize(currentLogmar, newE, patientDistance);
      }
    }
  }, [messageFromPatient]);

  useEffect(() => {
    if (isInitialLoad === true) {
      const currentGeneratedE = characterPositions[Math.floor(Math.random() * 4)];
      setCurrentE(currentGeneratedE);

      postMessage({
        type: 'randomE',
        content: {
          contentSize: Math.round(getLogmarContentSize(currentLogmar, distanceFromScreen)),
          distanceReferenceInMM: distanceFromScreen,
          logmarContent: currentGeneratedE,
          methodSelected: methodSelected,
          testMode: testMode,
        },
      });
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  return (
    <Flex centered column width='85vw' maxWidth='700px'>
      <Flex width='100%' justifyContent='space-between' alignItems={!smallScreen && 'center'}>
        <Flex
          centered
          border={`1px solid ${theme.colors.neutralLight}`}
          borderRadius='4px'
          width='55vw'
          height='50vh'
          maxHeight='400px'
          marginBottom='1rem'
          marginRight='1rem'
        >
          <Image src={currentE} alt='random-e' />
        </Flex>
        <Flex column height='50vh' width='30vw' maxHeight='200px' justifyContent='space-between' alignItems='center'>
          <Flex centered borderBottom='1px solid black' width='100%' padding='0.25rem 0'>
            <Text>{`logMAR Size: ${currentLogmar?.value?.toFixed(1) || 0}`}</Text>
          </Flex>
          <Button width='15em' maxWidth='30vw' onClick={() => selectNextLogmar()}>
            Correct
          </Button>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.negativeDark} onClick={() => markIncorrectAnswer()}>
            Incorrect
          </Button>
          <Button width='15em' maxWidth='30vw' backgroundColor={theme.colors.callToAction} onClick={() => markRepeatAnswer()}>
            Repeat
          </Button>
        </Flex>
      </Flex>
      <ResponsiveAcuityInfoPanel changeTestMode={changeTestMode} />
    </Flex>
  );
};

export default ResponsiveRandomETestOptician;
